import { useLang } from '../utils/i18n';
import {
  resolveMainToolContent,
  type MainToolId,
} from '../data/mainToolI18n';

/**
 * Concise, fully localized supporting content for non-English variants of the
 * six main tools. English pages retain their longer, approved copy verbatim.
 */
export default function MainToolLocalizedContent({ tool }: { tool: MainToolId }) {
  const { t } = useLang();
  const content = resolveMainToolContent(tool, t);

  return (
    <div className="border-t border-gray-100 dark:border-gray-800">
      <section className="py-12 bg-white dark:bg-gray-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white mb-6">
            {content.howTitle}
          </h2>
          <ol className="space-y-4">
            {content.steps.map((step, index) => (
              <li key={step} id={`localized-step-${index + 1}`} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                  {index + 1}
                </span>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-12 bg-gray-50 dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-violet-100 dark:border-violet-900/50 bg-violet-50/70 dark:bg-violet-950/20 p-5">
            <h2 className="text-lg font-extrabold text-gray-900 dark:text-white mb-2">
              {content.exampleTitle}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{content.example}</p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-5 sm:grid-cols-2">
          <article className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 p-5">
            <h2 className="text-lg font-extrabold text-gray-900 dark:text-white mb-2">
              {content.featuresTitle}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{content.features}</p>
          </article>
          <article className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 p-5">
            <h2 className="text-lg font-extrabold text-gray-900 dark:text-white mb-2">
              {content.limitationsTitle}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{content.limitations}</p>
          </article>
        </div>
      </section>

      <section className="py-12 bg-gray-50 dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white mb-5">
            {content.faqTitle}
          </h2>
          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {content.faqs.map(faq => (
              <article key={faq.q} className="py-4">
                <h3 className="font-bold text-gray-900 dark:text-white text-sm mb-1.5">{faq.q}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{faq.a}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
